import { useEffect, useState } from 'react';
import { disablePush, enablePush, isIOS, isStandalone, pushState, sendTestPush, type PushState } from '../lib/push';
import { ErrorNote } from './ui';

/** Enrols this device for push, and explains the iOS home-screen requirement. */
export default function NotificationCard(): JSX.Element {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tested, setTested] = useState<string | null>(null);

  useEffect(() => {
    pushState().then(setState).catch(() => setState('unsupported'));
  }, []);

  async function run(action: () => Promise<PushState>) {
    setBusy(true);
    setError(null);
    setTested(null);
    try {
      setState(await action());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    setTested(null);
    try {
      const res = await sendTestPush();
      setTested(res.delivered > 0 ? `Sent to ${res.delivered} device(s).` : 'No devices are subscribed yet.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h3>Notifications on this device</h3>
      <ErrorNote error={error} />

      {state === null && <p className="muted small">Checking…</p>}

      {state === 'needs-install' && (
        <div className="notice warn">
          <strong>Add this app to your Home Screen first.</strong>
          <p className="small" style={{ margin: '6px 0 0' }}>
            In Safari tap the Share button, then <em>Add to Home Screen</em>. Open the app from the new icon and come
            back here — iPhone only allows notifications from installed web apps.
          </p>
        </div>
      )}

      {state === 'unsupported' && (
        <p className="muted small">
          This browser cannot show notifications.{' '}
          {isIOS() && !isStandalone() && 'On iPhone, add the app to your Home Screen and open it from there.'}
        </p>
      )}

      {state === 'denied' && (
        <div className="notice bad small">
          Notifications are blocked. Turn them back on in Settings › Notifications › Corbett Chores, then reload.
        </div>
      )}

      {state === 'off' && (
        <>
          <p className="small muted">Get a nudge when chores are waiting.</p>
          <button className="btn block" type="button" disabled={busy} onClick={() => run(enablePush)}>
            Turn on notifications
          </button>
        </>
      )}

      {state === 'on' && (
        <>
          <p className="small">
            <span className="badge good">On</span> This device will receive reminders.
          </p>
          {tested && <p className="small muted">{tested}</p>}
          <div className="row">
            <button className="btn secondary grow" type="button" disabled={busy} onClick={test}>
              Send a test
            </button>
            <button className="btn ghost" type="button" disabled={busy} onClick={() => run(disablePush)}>
              Turn off
            </button>
          </div>
        </>
      )}
    </div>
  );
}
